/**
 * Iter 122c — Modale de suppression voyage (Kamel).
 *
 * 2 modes :
 *   - "Retrait public" (réversible) : retire du catalogue public mais garde
 *     toutes les fiches et stats côté gestion.
 *   - "Suppression intégrale" (irréversible) : supprime le voyage et TOUTES
 *     les données liées (inscriptions, paiements, bus, dépenses, ...).
 *     Demande la saisie EXACTE du titre pour valider (anti-erreur fatale).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => (typeof v === 'number'
  ? v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : v ?? '—');

const TripDeletionModal = ({ open, trip, onClose, getAuthConfig, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('public_only'); // public_only | complete
  const [confirmTitle, setConfirmTitle] = useState('');
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open || !trip) return;
    setMode('public_only');
    setConfirmTitle('');
    setError('');
    setDone(null);
    setPreview(null);
    setLoading(true);
    axios.get(`${API}/admin/trips/${trip.id}/deletion-preview`, getAuthConfig())
      .then(r => setPreview(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [open, trip, getAuthConfig]);

  if (!open || !trip) return null;

  const exec = async () => {
    setError('');
    setActing(true);
    try {
      let r;
      if (mode === 'public_only') {
        if (trip.is_hidden_public) {
          r = await axios.post(`${API}/admin/trips/${trip.id}/restore-public`, {}, getAuthConfig());
          setDone({ kind: 'restored', data: r.data });
        } else {
          r = await axios.post(`${API}/admin/trips/${trip.id}/delete-public`, {}, getAuthConfig());
          setDone({ kind: 'hidden', data: r.data });
        }
      } else {
        r = await axios.post(
          `${API}/admin/trips/${trip.id}/delete-complete`,
          { confirm_title: confirmTitle },
          getAuthConfig(),
        );
        setDone({ kind: 'deleted', data: r.data });
      }
      if (onDone) onDone(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setActing(false);
  };

  const counts = preview?.counts || {};
  const totals = preview?.totals || {};
  const isAlreadyHidden = !!trip.is_hidden_public;
  const completeReady = mode === 'complete' && confirmTitle.trim() === (trip.title || '').trim();

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" data-testid="trip-deletion-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="bg-red-600 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="font-bold text-lg">🗑 Suppression du voyage</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="trip-del-close">×</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
            <div className="font-bold text-[#1E3A5F]">{trip.title}</div>
            <div className="text-gray-600 text-xs">{trip.destination} · {trip.start_date} → {trip.end_date}</div>
            {isAlreadyHidden && (
              <div className="mt-1 inline-block px-2 py-0.5 bg-amber-100 border border-amber-400 text-amber-800 text-[10px] rounded font-bold">
                ⊘ Actuellement masqué du public
              </div>
            )}
          </div>

          {done ? (
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4" data-testid="trip-del-done">
              <div className="font-bold text-emerald-800">
                {done.kind === 'hidden' && '✓ Voyage retiré du catalogue public'}
                {done.kind === 'restored' && '✓ Voyage à nouveau visible publiquement'}
                {done.kind === 'deleted' && '✓ Voyage supprimé intégralement'}
              </div>
              {done.kind === 'deleted' && done.data?.deleted && (
                <ul className="text-xs text-gray-700 mt-2 space-y-0.5">
                  {Object.entries(done.data.deleted).map(([k, v]) => (
                    <li key={k}>• {k} : <strong>{v}</strong> supprimé{v > 1 ? 's' : ''}</li>
                  ))}
                </ul>
              )}
              <button onClick={onClose} className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded text-sm font-bold" data-testid="trip-del-close-after">
                Fermer
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-800" data-testid="trip-del-error">❌ {error}</div>
              )}

              {loading && <div className="text-center text-sm text-gray-500 py-4">⏳ Chargement de l'aperçu…</div>}

              {!loading && preview && (
                <>
                  <div className="border border-gray-300 rounded-lg p-3 text-xs">
                    <h3 className="font-bold text-[#1E3A5F] mb-2">📊 Données liées au voyage</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <Stat label="Inscriptions" v={counts.registrations} testId="count-regs" />
                      <Stat label="Règlements" v={counts.reglements} />
                      <Stat label="Plans bus" v={counts.bus_plans} />
                      <Stat label="Dépenses" v={counts.depenses} />
                      <Stat label="Liste attente" v={counts.waitlist} />
                      <Stat label="Factures custom" v={counts.custom_invoices} />
                      <Stat label="Interactions" v={counts.voyageur_interactions} />
                      <Stat label="Pièces ID" v={counts.identite_files} />
                      <Stat label="Formules d'inscription" v={counts.form_destinations} testId="count-fds" />
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-200 grid grid-cols-3 gap-2 text-[11px]">
                      <Money label="Total voyage" v={totals.total_voyage} />
                      <Money label="Total payé" v={totals.total_paye} color="text-emerald-700" />
                      <Money label="Total dépenses" v={totals.total_depenses} color="text-red-700" />
                    </div>
                  </div>

                  {/* Choix du mode */}
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-amber-50 hover:border-amber-400">
                      <input type="radio" name="del-mode" checked={mode === 'public_only'} onChange={() => setMode('public_only')}
                        className="mt-1" data-testid="del-mode-public" />
                      <div className="flex-1">
                        <div className="font-bold text-amber-700 text-sm">
                          ⊘ Retrait public uniquement {isAlreadyHidden ? '(restaurer)' : ''}
                        </div>
                        <div className="text-xs text-gray-600">
                          {isAlreadyHidden
                            ? 'Le voyage est actuellement masqué : cliquer pour le rendre à nouveau visible sur le site public.'
                            : 'Retire du catalogue public (le site web). Les inscriptions, paiements, bus, stats restent intacts côté gestion. Réversible en 1 clic.'}
                        </div>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-red-50 hover:border-red-400">
                      <input type="radio" name="del-mode" checked={mode === 'complete'} onChange={() => setMode('complete')}
                        className="mt-1" data-testid="del-mode-complete" />
                      <div className="flex-1">
                        <div className="font-bold text-red-700 text-sm">🗑 Suppression intégrale (corbeille 7 jours)</div>
                        <div className="text-xs text-gray-600">
                          Supprime le voyage <strong>ET</strong> toutes les données liées : inscriptions, règlements, plans bus,
                          dépenses, liste d'attente, factures, pièces d'identité. <strong>Comme s'il n'avait jamais existé</strong>
                          (sort du bilan annuel et de toutes les stats).
                        </div>
                        <div className="text-[11px] text-emerald-700 mt-1">
                          ✓ Filet de sécurité : tout reste récupérable depuis l'onglet <strong>"🗑 Corbeille voyages"</strong> pendant 7 jours,
                          puis purge définitive.
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Saisie de confirmation pour mode complet */}
                  {mode === 'complete' && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-3 text-sm">
                      <div className="font-bold text-red-800 mb-2">
                        ⚠ Pour confirmer, saisis EXACTEMENT le titre du voyage :
                      </div>
                      <code className="block bg-white p-2 rounded border border-red-200 text-xs mb-2">{trip.title}</code>
                      <input
                        type="text"
                        value={confirmTitle}
                        onChange={(e) => setConfirmTitle(e.target.value)}
                        placeholder="Recopie le titre ici"
                        className="w-full px-3 py-2 border-2 border-red-300 rounded text-sm font-mono"
                        data-testid="del-confirm-title"
                      />
                      {confirmTitle && !completeReady && (
                        <div className="text-[11px] text-red-700 mt-1">Le titre saisi ne correspond pas exactement.</div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between gap-3 pt-2">
                    <button onClick={onClose} className="px-4 py-2 border rounded text-sm" data-testid="del-cancel">
                      Annuler
                    </button>
                    <button
                      onClick={exec}
                      disabled={acting || (mode === 'complete' && !completeReady)}
                      data-testid="del-execute"
                      className={`px-6 py-2 rounded text-sm font-bold text-white transition ${
                        acting ? 'bg-gray-400' :
                        mode === 'complete' ? 'bg-red-600 hover:bg-red-700 disabled:bg-gray-300' :
                        'bg-amber-600 hover:bg-amber-700'
                      }`}
                    >
                      {acting ? '⏳ En cours…' :
                        mode === 'public_only'
                          ? (isAlreadyHidden ? '↻ Restaurer la visibilité' : '⊘ Retirer du public')
                          : '🗑 Supprimer intégralement'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, v, testId }) => (
  <div className="bg-slate-50 border border-slate-200 rounded p-2" data-testid={testId}>
    <div className="text-[9px] text-gray-500 uppercase">{label}</div>
    <div className="font-bold text-[#1E3A5F]">{v ?? 0}</div>
  </div>
);

const Money = ({ label, v, color = 'text-gray-800' }) => (
  <div>
    <div className="text-[9px] text-gray-500 uppercase">{label}</div>
    <div className={`${color} font-mono font-semibold`}>{fmt(v)} €</div>
  </div>
);

export default TripDeletionModal;
